import { xor } from 'lodash';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Category, MovieData } from '@amzn/awsconcierge-magic-movie-machine-type-script-client';

import Tabs from './Tabs';
import { movieGridTabs as tabs } from '../constants';

import Movie from './Movie';

import {
  getMagicMovieUserId,
  getMagicMovieUserName,
  getSelectedMovies,
  getSelectedMoviesForTab,
  setMagicMovieSelectedMovies,
  setMagicMovieRecommendedMovies,
  setMagicMovieSelectedMoviesForTab,
} from 'store/magicMovie';
import { useAppDispatch, useAppSelector } from 'store/utils/hooks';

import { movieCatalog } from './moviesData';
import { movieGridView, PopularMoviesCategory } from '../constants';
import { getRecommendedMovies, putMovieRecommendations } from 'services/howser/movieApi';

interface PopularMoviesProps {
  onChangeView: (view: string) => void;
}

const getSelectedMoviesSet = (setName: string) => {
  return movieCatalog[setName];
};

export const MagicMoviePopularMovies: React.FC<PopularMoviesProps> = ({ onChangeView }) => {
  const { t } = useTranslation(['magicMovie']);
  const dispatch = useAppDispatch();
  const selectedMovies = useAppSelector(getSelectedMovies);
  const userName = useAppSelector(getMagicMovieUserName);
  const userId = useAppSelector(getMagicMovieUserId);
  const selectedMoviesCountForTab = useAppSelector(getSelectedMoviesForTab);
  const [selectedSet, setSelectedSet] = useState<string>(PopularMoviesCategory.action);
  const [selectedMovieSet, setSelectedMovieSet] = useState<Partial<MovieData>[]>(
    getSelectedMoviesSet(PopularMoviesCategory.action)
  );
  const [heightValueOnResize, setHeightValueOnResize] = useState(300);
  const selectedMovieQuantity = selectedMovies.length;
  const hasMinSelectedMovies = selectedMovieQuantity >= 5;
  const gridheaderRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const domElement = document.getElementById('popular-movie-outer-fixed-header');
    setHeightValueOnResize(domElement?.offsetHeight || 300);
    window.addEventListener('resize', function () {
      if (domElement?.offsetHeight) {
        setHeightValueOnResize(domElement?.offsetHeight);
      } else {
        setHeightValueOnResize(300);
      }
    });
  }, [hasMinSelectedMovies]);

  const onMovieClick = (id: string) => {
    let currentCount = selectedMoviesCountForTab[selectedSet] || 0;
    if (selectedMovies.includes(id)) {
      currentCount -= 1;
    } else {
      currentCount += 1;
    }
    dispatch(
      setMagicMovieSelectedMoviesForTab({
        selectedMoviesForTab: {
          [selectedSet]: currentCount,
        },
      })
    );
    dispatch(
      setMagicMovieSelectedMovies({
        selectedMovies: xor(selectedMovies, [id]),
      })
    );
  };

  const onTabChange = (selectedSet: string) => {
    const selectedMovieSet = getSelectedMoviesSet(selectedSet);
    setSelectedSet(selectedSet);
    setSelectedMovieSet(selectedMovieSet);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const getMovieRecommendations = () => {
    Object.values(Category).forEach((category: Category) => {
      getRecommendedMovies(userId, category)
        .then((response) => {
          dispatch(
            setMagicMovieRecommendedMovies({
              recommendedMovies: {
                [category]: response.movieList,
              },
            })
          );
        })
        .catch((error) => {
          return error;
        });
    });
  };

  const putMoviesRecommendations = () => {
    const userSelectedMovies = _.chunk(selectedMovies, 10);
    userSelectedMovies.forEach((moviesList) => {
      putMovieRecommendations(userId, moviesList)
        .then((res) => {
          return res;
        })
        .catch((error) => {
          return error;
        });
    });
  };

  return (
    <div id="game" className="movies-root-padding" data-test-id="magicMovieMovieGridRoot">
      <div id="popular-movie-selection-grid-container">
        <div
          id="popular-movie-outer-fixed-header"
          className="movie-grid-fixed-header"
          ref={gridheaderRef}
          style={{ top: -(heightValueOnResize - 158) }}
        >
          <h1>{hasMinSelectedMovies ? t('popularMovies.header1') : t('popularMovies.header2')}</h1>
          <div id="popular-movie-inner-header" className="movie-grid-inner-header-container">
            <p className="blue" style={{ marginBlockEnd: '0.5em' }}>
              {hasMinSelectedMovies
                ? t('popularMovies.header1Desc', { userName: userName })
                : t('popularMovies.header2Desc', { userName: userName })}
            </p>
            <button
              id="see-recommendation-button"
              className={`customButton primary movie-grid-header-button ${selectedMovieQuantity >= 5 && 'animate'}`}
              onClick={() => {
                onChangeView(movieGridView.diagram);
                putMoviesRecommendations();
                setTimeout(function () {
                  getMovieRecommendations();
                }, 1000);
              }}
              disabled={selectedMovieQuantity < 5}
              data-analytics-type="eventDetail"
              data-analytics="howserSeeRecommendationsButton"
              data-test-id="magicMovieMovieGridRecommendationsButton"
            >
              {selectedMovieQuantity < 5
                ? t('popularMovies.buttonLabel1', { selectedMovieQuantity: selectedMovieQuantity })
                : t('popularMovies.buttonLabel2')}
            </button>
          </div>
          <Tabs
            tabs={tabs}
            selectedSetCount={selectedMoviesCountForTab}
            selectedTab={selectedSet}
            onTabChange={onTabChange}
          />
        </div>
        <div id="popular-movies-container">
          <div
            id="movie_content"
            className="movies-list-container"
            style={{
              marginTop: heightValueOnResize + 15 + 'px',
            }}
          >
            {selectedMovieSet.map((movie) => {
              return (
                <Movie
                  id={movie?.movieId ? movie.movieId : ''}
                  key={movie.movieId}
                  clickEvents
                  selectedMovies={selectedMovies}
                  image={movie?.imageUrl ? movie.imageUrl : ''}
                  title={movie?.name ? movie.name : ''}
                  genre={movie?.genres?.[0] ? movie.genres[0] : ''}
                  onMovieClick={onMovieClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicMoviePopularMovies;
